import { parsePhoneNumberWithError } from 'libphonenumber-js';
import chroma from 'chroma-js';
import DOMPurify from 'dompurify';
import { default as currencyLib } from 'currency-codes';

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const phoneRegex = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

export const maskString = (str = ''): string => {
  if (!str || str.length <= 2) return str;

  const firstChar = str[0];
  const lastChar = str[str.length - 1];
  const maskedChars = '*'.repeat(str.length - 2);

  return firstChar + maskedChars + lastChar;
};

export const maskWords = (words: (string | undefined)[], separator = ' '): string => {
  const mask: string[] = [];

  words.forEach((word) => {
    if (!word) return;

    if (word.length <= 2) {
      mask.push(word);
    } else {
      const firstChar = word.charAt(0);
      const lastChar = word.charAt(word.length - 1);
      const maskedChars = '*'.repeat(word.length - 2);

      mask.push(firstChar + maskedChars + lastChar);
    }
  });

  return mask.join(separator);
};

export const stripHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    RETURN_TRUSTED_TYPE: false,
  }).trim();
};

export const maskEmail = (emailStr = ''): string => {
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (!emailStr || !emailStr.includes('@')) return emailStr;

  const [localPart, domain] = emailStr.split('@');
  const lastDotIndex = domain.lastIndexOf('.');
  const domainName = domain.slice(0, lastDotIndex);
  const tld = domain.slice(lastDotIndex + 1);

  return `${maskString(localPart)}@${maskString(domainName)}.${tld}`;
};

export const sumArray = (list: number[] | null): any => {
  return (list ?? []).reduce((acc, curr) => acc + (curr || 0), 0);
};

export function formatCurrency({
  value,
  currency = 'USD',
  compact = false,
  integer = false,
  majorUnit = false,
}: {
  value?: number | null;
  currency?: string | null;
  compact?: boolean;
  integer?: boolean;
  majorUnit?: boolean;
}): string {
  if (value === null || value === undefined) return '';

  const currencyRecord = currencyLib.code(currency ?? 'USD')!;

  const decimalFactor = majorUnit ? 1 : 10 ** currencyRecord.digits;
  const monetaryAmount = value / decimalFactor;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyRecord.code,
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: integer ? 0 : currencyRecord.digits,
  });

  return formatter.format(monetaryAmount);
}

/**
 * Convert an amount from smallest units (e.g., cents) to standard units (e.g., dollars).
 * Handles currency-specific decimal places (e.g., JPY has 0 decimals, USD has 2).
 */
export function toStandardUnit(
  value: number | null | undefined,
  currency: string | null = 'USD',
): number | undefined {
  if (value === null || value === undefined) return undefined;

  const currencyRecord = currencyLib.code(currency ?? 'USD')!;
  const decimalFactor = 10 ** currencyRecord.digits;

  return value / decimalFactor;
}

/**
 * Convert an amount from standard units (e.g., dollars) to smallest units (e.g., cents).
 * Handles currency-specific decimal places (e.g., JPY has 0 decimals, USD has 2).
 */
export function toSmallestUnit(
  value: number | null | undefined,
  currency: string | null = 'USD',
): number | undefined {
  if (value === null || value === undefined) return undefined;

  const currencyRecord = currencyLib.code(currency ?? 'USD')!;
  const decimalFactor = 10 ** currencyRecord.digits;

  return Math.round(value * decimalFactor);
}

export function formatEmail(email: string | null): string | null {
  if (!email) return null;

  return email;
}

export function formatPhoneNumber(phoneNumber: string | null): string | null {
  if (!phoneNumber) return null;

  try {
    return parsePhoneNumberWithError(phoneNumber).formatInternational();
  } catch (e) {
    return phoneNumber;
  }
}

export const validateEmail = (email?: string): boolean => {
  if (!email) return false;

  return emailRegex.test(email?.trim());
};

const padString = (s: string): string => (s.length === 1 ? `0${s}` : s);

export function formatDuration(duration: number | null): string {
  if (duration === null) return '00:00';
  const s = Math.floor(duration);
  const minutes = Math.floor(s / 60).toString();
  const remainder = (s % 60).toString();

  return `${padString(minutes)}:${padString(remainder)}`;
}

type IndexOfCheckCallable<T> = (v: T) => any;

interface IndexOfCheckItem<T> {
  item: T;
  key: keyof T;
}

type IndexOfCheck<T> = IndexOfCheckCallable<T> | IndexOfCheckItem<T>;

export function indexOf<T extends any>(array: T[], check: IndexOfCheck<T>): number {
  if (typeof check !== 'function') {
    const checkOptions = check as IndexOfCheckItem<T>; // eslint-disable-line @typescript-eslint/no-unnecessary-type-assertion
    check = (element: T) => element[checkOptions.key] === checkOptions.item[checkOptions.key];
  }

  return array.findIndex((v: T) => Boolean((check as IndexOfCheckCallable<T>)(v)));
}

export function findItem<T extends any>(
  array: T[],
  check: IndexOfCheck<T>,
  options?: { splice?: boolean },
): T | null {
  const idx = indexOf(array, check);
  const entity = idx !== -1 ? array[idx] : null;
  if (idx !== -1 && options?.splice) {
    array.splice(idx, 1);
  }

  return entity;
}

export function humanize(text?: string): string {
  if (!text) return '';

  const result = text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .replace(/_+|-+/g, ' ');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function formatFileSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

export async function waitFor(cond: () => boolean, ms = 1000): Promise<boolean> {
  while (!cond()) await sleep(ms);
  return Promise.resolve(cond());
}

export async function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Interpolates between a list of colors based on a factor t.
 *
 * @param colors - An array of color strings (e.g., hex, RGB).
 * @param t - A number between 0 and 1 (inclusive) representing the interpolation factor.
 *            Values outside this range will be clamped.
 * @returns The interpolated color as a hex string.
 */
export function interpolateColors(colors: string[], t: number): string {
  const clamped = Math.max(0, Math.min(1, t));

  const scale = chroma.scale(colors).domain([0, 1]);
  return scale(clamped).hex();
}

/**
 * Round a number to the nearest whole power of 10
 *
 * Usage:
 * roundCeilWholeTen(224) -> 300
 * roundCeilWholeTen(31221) -> 40000
 * roundCeilWholeTen(650, { even: true }) -> 800 // rather than 700
 */
export function roundCeilWholeTen(value: number, options?: { even: boolean }): number {
  const log10 = Math.max(Math.floor(Math.log10(value)), 1);
  const roundTo = 10 ** log10 * (Math.floor(value / 10 ** log10) + 1);

  let roundedValue = Math.ceil(value / roundTo) * roundTo;

  if (options?.even && (roundedValue / 10 ** log10) % 2 !== 0) {
    roundedValue += 10 ** log10;
  }

  return roundedValue;
}
